import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardShell } from "@/components/Shell";
import { Loader2, QrCode, Save, User as UserIcon } from "lucide-react";

export default function StaffProfileEdit() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", bio: "", photo_url: "" });
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");
  const [qr, setQr] = useState(null);

  useEffect(() => {
    if (user) setForm({ name: user.name || "", bio: user.bio || "", photo_url: user.photo_url || "" });
    if (user?.id) {
      const baseUrl = window.location.origin;
      api.get(`/qr/staff/${user.id}`, { params: { base_url: baseUrl } }).then((r) => setQr(r.data));
    }
  }, [user]);

  async function save() {
    setSaving(true);
    setStatus("");
    try {
      const { data } = await api.put("/staff/me", form);
      setUser(data);
      setStatus("Gespeichert ✓");
    } catch {
      setStatus("Fehler beim Speichern");
    } finally {
      setSaving(false);
      setTimeout(() => setStatus(""), 2500);
    }
  }

  return (
    <DashboardShell role="staff">
      <div className="mb-8">
        <div className="text-xs tracking-[0.25em] uppercase text-[#EBB300]">Dein Profil</div>
        <h1 className="text-4xl md:text-5xl font-black tracking-tight mt-2 flex items-center gap-3"><UserIcon className="w-8 h-8 text-[#EBB300]" /> Profil bearbeiten</h1>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4 bg-[#121216] border border-white/5 rounded-2xl p-6">
          <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="staff-profile-name" />
          <Input label="Foto-URL" value={form.photo_url} onChange={(v) => setForm({ ...form, photo_url: v })} testid="staff-profile-photo" placeholder="https://..." />
          <div>
            <span className="text-xs tracking-[0.15em] uppercase text-[#A1A1AA] mb-2 block">Kurze Bio</span>
            <textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              data-testid="staff-profile-bio"
              rows={4}
              className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#EBB300] resize-none"
              placeholder="Stell dich kurz vor…"
            />
          </div>
          <div className="flex items-center gap-4 pt-2">
            <button onClick={save} disabled={saving} data-testid="staff-profile-save"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#EBB300] text-[#0A0A0C] font-bold hover:bg-[#FAD02C] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Speichern</>}
            </button>
            {status && <span className="text-sm text-[#22C55E]" data-testid="staff-profile-status">{status}</span>}
          </div>
        </div>

        {/* Live preview + QR */}
        <div className="space-y-4">
          <div className="bg-[#121216] border border-white/5 rounded-2xl p-5 text-center">
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-4">Vorschau</div>
            {form.photo_url && <img src={form.photo_url} alt={form.name} className="w-24 h-24 rounded-full object-cover mx-auto mb-3 gold-glow" />}
            <div className="font-black text-xl tracking-tight">{form.name || "Dein Name"}</div>
            <div className="text-sm text-[#A1A1AA] mt-2">{form.bio || "Deine Bio erscheint hier."}</div>
          </div>

          <div className="bg-[#121216] border border-white/5 rounded-2xl p-5 text-center">
            <div className="text-xs tracking-[0.2em] uppercase text-[#A1A1AA] mb-3 flex items-center justify-center gap-2"><QrCode className="w-3.5 h-3.5" /> Dein persönlicher QR</div>
            {qr ? (
              <>
                <img src={qr.image} alt="QR" className="w-48 h-48 mx-auto rounded-xl" data-testid="staff-qr-image" />
                <div className="text-[10px] text-[#52525B] mt-3 break-all">{qr.url}</div>
              </>
            ) : (
              <div className="h-48 flex items-center justify-center text-[#52525B]"><Loader2 className="animate-spin w-5 h-5" /></div>
            )}
          </div>
        </div>
      </div>
    </DashboardShell>
  );
}

function Input({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block">
      <span className="text-xs tracking-[0.15em] uppercase text-[#A1A1AA] mb-2 block">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#EBB300]" {...rest} />
    </label>
  );
}
